import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiMenu, FiX } from 'react-icons/fi';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const gtranslateScriptLoaded = useRef(false); // To ensure script is loaded only once

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (location.pathname === '/' && location.hash) {
            const targetId = location.hash.substring(1);
            const targetElement = document.getElementById(targetId);
            if (targetElement) {
                setTimeout(() => {
                    targetElement.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
    }, [location]);

    // Effect to load GTranslate script
    useEffect(() => {
        if (gtranslateScriptLoaded.current) return; // Don't load script multiple times

        // Define GTranslate settings
        window.gtranslateSettings = {
            "default_language": "en",
            "detect_browser_language": true,
            "languages": ["en", "ro"], // Your specified languages
            "wrapper_selector": ".gtranslate_wrapper", // Class for the widget placeholder
            "flag_size": 24,
            "switcher_horizontal_position": "inline" // Ensures it tries to fit inline
        };

        // Create and append the GTranslate script
        const script = document.createElement('script');
        script.src = "https://cdn.gtranslate.net/widgets/latest/dwf.js";
        script.defer = true;
        document.head.appendChild(script); // Appending to head is common for scripts

        gtranslateScriptLoaded.current = true;
    }, []);


    const navLinks = [
        { name: 'About', href: '/about' },
        { name: 'Services', href: '/services' },
        { name: 'Projects', href: '/projects' },
        { name: 'Collaborators', href: '#collaborators', isHash: true },
        { name: 'Room Types', href: '#room-types', isHash: true },
        { name: 'Construction', href: '#construction', isHash: true },
        { name: 'FAQ', href: '#faq', isHash: true },
        { name: 'Location', href: '#location', isHash: true },
        { name: 'Contact Us', href: '#contact', isHash: true }
    ];

    const handleNavLinkClick = (href, isHash) => {
        setIsOpen(false);
        if (isHash) {
            navigate(`/${href}`);
        } else {
            navigate(href);
        }
    };

    return (
        <motion.nav
            className={`fixed w-full z-50 transition-all duration-300 ${
                scrolled
                    ? 'bg-arcdom-navy bg-opacity-95 shadow-md py-2'
                    : 'bg-white bg-opacity-85 py-4'
            }`}
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className="container mx-auto px-4 flex justify-between items-center">
                {/* Logo */}
                <Link to="/" className={`font-bold text-2xl font-fira ${
                    scrolled ? 'text-white' : 'text-blue-900'
                }`}>
                    ARCDOM
                </Link>

                {/* Desktop Navigation */}
                <div className="hidden xl:flex items-center space-x-6" id='translation'>
                    {navLinks.map((link, index) => (
                        <button
                            key={index}
                            onClick={() => handleNavLinkClick(link.href, link.isHash)}
                            className={`font-montserrat ${
                                scrolled ? 'text-white' : 'text-black'
                            } hover:text-yellow-500 transition-colors duration-300 cursor-pointer`}
                        >
                            {link.name}
                        </button>
                    ))}
                    {/* GTranslate Desktop Wrapper */}
                    <div className="gtranslate_wrapper ml-2"></div>
                </div>

                {/* Mobile Navigation Button and Language Dropdown */}
                <div className="xl:hidden flex items-center space-x-3">
                    {/* GTranslate Mobile Wrapper - Now outside the mobile menu */}
                    <div className="gtranslate_wrapper mobile-translate"></div>

                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="focus:outline-none z-50"
                    >
                        {isOpen ? (
                            <FiX className="h-6 w-6 text-white" />
                        ) : (
                            <FiMenu className={`h-6 w-6 ${scrolled ? 'text-white' : 'text-blue-900'}`} />
                        )}
                    </button>
                </div>

                {/* Mobile Navigation Menu */}
                {isOpen && (
                    <motion.div
                        className="fixed inset-0 bg-blue-900 z-40 pt-20 pb-10 overflow-y-auto max-w-full"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="flex flex-col items-center space-y-6 py-8">
                            {navLinks.map((link, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleNavLinkClick(link.href, link.isHash)}
                                    className="text-white font-montserrat text-xl hover:text-yellow-500 transition-colors duration-300 cursor-pointer"
                                >
                                    {link.name}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </div>
        </motion.nav>
    );
};

export default Navbar;
